"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Check, Sparkles, Star, Clock, Shield, DollarSign } from "lucide-react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Footer } from "@/components/Footer";

// Add subscription type to the pricing plan
const pricingPlans = [
  {
    name: "Free",
    price: "$0",
    description: "Start planning your dream trip today",
    features: [
      "Up to 25 AI trip generations credits per month",
      "Save and organize up to 3 trips",
      "Basic trip planning features",
    ],
    buttonText: "Start Free",
    priceId: "free",
    subscriptionType: "free"
  },
  {
    name: "Pro",
    price: "$9.99",
    discountedPrice: "$4.99",
    description: "Unlock the full power of AI travel planning",
    features: [
      "Up to 200 AI trip generations credits per month",
      "Save and organize up to 30 trips",
      "Early access to new AI features",
      "24/7 priority customer support",
    ],
    buttonText: "Upgrade to Pro",
    subscribedButtonText: "Current Plan ✓",
    priceId: "pro_monthly",
    popular: true,
    subscriptionType: "pro"
  },
];

const testimonials = [
  {
    name: "Joyce L.",
    text: "Pro plan saved me hours of planning for my Europe trip!",
    rating: 5,
    image: "profile1.png",
  },
  {
    name: "James L.",
    text: "The AI suggestions were spot-on for my budget and preferences.",
    rating: 5,
    image: "profile2.png",
  },
];

interface UserData {
  planTier: string;
}


export default function PricingPage() {
  const { data: session } = useSession();
  const [loading, setLoading] = useState("");
  const [userData, setUserData] = useState<UserData | null>(null);
  const router = useRouter();
  
  useEffect(() => {
    const fetchUserData = async () => {
      if (session?.user?.id) {
        try {
          const response = await fetch('/api/user');
          if (!response.ok) throw new Error('Failed to fetch user data');
          const data = await response.json();
          setUserData(data);
        } catch (err) {
          console.error(err);
        }
      }
    };

    fetchUserData();
  }, [session?.user?.id]);
  
  // Check if user is subscribed to pro
  const isProSubscriber = userData?.planTier === "PRO";

  const handleUpgrade = async (priceId: string, currentPlan: boolean) => {
    // If user is already on this plan, don't do anything
    if (currentPlan) {
      return;
    }

    if (priceId === "free") {
      router.push("/create-trip");
      return;
    }

    if (!session) {
      window.location.href = "/api/auth/signin";
      return;
    }

    try {
      setLoading(priceId);
      const response = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          priceId,
        }),
      });

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading("");
    }
  };

  return (
    <div className="container mx-auto px-4 pb-24 pt-16 sm:pt-28">
      {/* Header section remains the same */}
      <div className="text-center mb-8 sm:mb-16">
        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          Plan Your Perfect Trip with AI
        </h1>
        <p className="text-xl text-gray-600 mb-6">
          Join thousands of happy travelers who plan smarter, not harder
        </p>

        <div className="hidden sm:flex justify-center items-center gap-8 mb-8">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-indigo-600" />
            <span>Save 8+ hours per trip</span>
          </div>
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-indigo-600" />
            <span>Limited time discount</span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-indigo-600" />
            <span>Top user rating</span>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
        {pricingPlans.map((plan) => {
          const isCurrentPlan = isProSubscriber && plan.subscriptionType === "pro";
          
          return (
            <Card
              key={plan.name}
              className={`relative flex flex-col h-full rounded-2xl ${
                plan.popular
                  ? "ring-2 ring-indigo bg-indigo-50 shadow-lg scale-100"
                  : ""
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-medium px-4 py-1.5 rounded-full flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4" />
                    Most Popular
                  </span>
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription className="text-lg">
                  {plan.description}
                </CardDescription>
                <div className="mt-4 flex items-center gap-3">
                  {plan.discountedPrice ? (
                    <>
                      <span className="text-4xl font-bold text-primary">
                        {plan.discountedPrice}
                      </span>
                      <span className="text-2xl font-medium text-gray-400 line-through">
                        {plan.price}
                      </span>
                      <span className="text-sm font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded">
                        Limited Time Offer
                      </span>
                    </>
                  ) : (
                    <span className="text-4xl font-bold">{plan.price}</span>
                  )}
                </div>
                {plan.price !== "$0" && (
                  <div className="text-sm text-gray-500">
                    per month, cancel anytime
                  </div>
                )}
              </CardHeader>
              <CardContent className="flex-grow">
                <ul className="space-y-4">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check
                        className={`w-5 h-5 mt-1 ${
                          plan.popular ? "text-primary" : "text-green-500"
                        }`}
                      />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="mt-auto">
                <Button
                  className={`w-full py-6 text-lg ${
                    isCurrentPlan
                      ? "bg-green-700 hover:bg-green-700 text-white"
                      : plan.popular
                      ? "bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
                      : ""
                  }`}
                  variant={plan.popular || isCurrentPlan ? "default" : "outline"}
                  onClick={() => handleUpgrade(plan.priceId, isCurrentPlan)}
                  disabled={loading === plan.priceId || isCurrentPlan}
                >
                  {loading === plan.priceId
                    ? "Processing..."
                    : isCurrentPlan
                    ? plan.subscribedButtonText
                    : plan.buttonText}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* Rest of the component remains the same */}
      {/* Social Proof Section */}
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-center mb-8">
          What Our Users Say
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="bg-white/60 backdrop-blur-sm rounded-2xl p-8 shadow-lg border border-gray-100"
            >
              <div className="flex gap-1 mb-2">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 fill-yellow-400 text-yellow-400"
                  />
                ))}
              </div>
              <p className="text-gray-700 mb-2">"{testimonial.text}"</p>
              <div className="flex flex-row items-center gap-2">
                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-indigo-400 to-purple-400">
                  <Image
                    src={testimonial.image}
                    alt={`${testimonial.name}`}
                    height={40}
                    width={40}
                    className="h-10 w-10 rounded-full"
                    unoptimized={true}
                  />
                </div>
                <p className="font-medium">{testimonial.name}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
            <Footer />
    </div>
  );
}